import React, { useState, useEffect, useRef } from 'react';
import {
  Lock, Unlock, Fingerprint, ScanFace, KeyRound, Eye, EyeOff,
  CheckCircle2, AlertCircle, RefreshCw, LogOut, Smartphone
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';

export default function VaultLockScreen({ onUnlock }) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const {
    user,
    logout,
    reauthenticateWithMasterPassword,
    unlockVaultWithBiometrics,
    reauthenticateWithPasskey
  } = useAuth();

  // Detect real device biometric capability
  const [biometricAvailable, setBiometricAvailable] = useState(null);
  const [biometricLabel, setBiometricLabel] = useState('Biometrics');

  useEffect(() => {
    let cancelled = false;
    async function detectBiometric() {
      try {
        if (!window.PublicKeyCredential) {
          if (!cancelled) setBiometricAvailable(false);
          return;
        }
        const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
        if (!cancelled) {
          setBiometricAvailable(available);
          if (available) {
            const ua = navigator.userAgent.toLowerCase();
            const isIOS = /iphone|ipad|ipod/.test(ua);
            const isMac = /mac/.test(ua) && !isIOS;
            const isAndroid = /android/.test(ua);
            if (isIOS || isMac) setBiometricLabel('Face ID / Touch ID');
            else if (isAndroid) setBiometricLabel('Fingerprint / Face Unlock');
            else setBiometricLabel('Windows Hello / Passkey');
          }
        }
      } catch {
        if (!cancelled) setBiometricAvailable(false);
      }
    }
    detectBiometric();
    return () => { cancelled = true; };
  }, []);

  // Active view: 'biometric' or 'password'
  const [authMode, setAuthMode] = useState(() =>
    localStorage.getItem('pm_biometric_enabled') === 'true' ? 'biometric' : 'password'
  );

  // When capability resolves, correct the mode
  useEffect(() => {
    if (biometricAvailable === false && authMode === 'biometric') setAuthMode('password');
  }, [biometricAvailable, authMode]);

  const [isScanning, setIsScanning] = useState(false);
  const [scanSuccess, setScanSuccess] = useState(false);
  const [scanError, setScanError] = useState('');

  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [enableBiometricsNext, setEnableBiometricsNext] = useState(true);

  const hasAutoScannedRef = useRef(false);

  // Trigger real native WebAuthn prompt — no fake scanner animation
  const triggerBiometricScan = async () => {
    if (isScanning || scanSuccess) return;
    setIsScanning(true);
    setScanError('');
    setScanSuccess(false);
    if (navigator.vibrate) navigator.vibrate(30);
    try {
      const unlocked = await unlockVaultWithBiometrics(false);
      if (unlocked) {
        setScanSuccess(true);
        setIsScanning(false);
        if (navigator.vibrate) navigator.vibrate([40, 60, 100]);
        setTimeout(() => onUnlock?.(), 800);
        return;
      }
      const passkeyOk = await reauthenticateWithPasskey();
      if (passkeyOk) {
        setScanSuccess(true);
        setIsScanning(false);
        if (navigator.vibrate) navigator.vibrate([40, 60, 100]);
        setTimeout(() => onUnlock?.(), 800);
        return;
      }
      setIsScanning(false);
      setScanError(`${biometricLabel} verification cancelled or unavailable. Please use your Master Password.`);
    } catch (err) {
      setIsScanning(false);
      setScanError(err?.message || 'Biometric verification failed.');
    }
  };

  // Auto-trigger biometric once when mode is biometric and device supports it
  useEffect(() => {
    if (authMode === 'biometric' && biometricAvailable === true && !hasAutoScannedRef.current) {
      hasAutoScannedRef.current = true;
      const timer = setTimeout(() => { triggerBiometricScan(); }, 500);
      return () => clearTimeout(timer);
    }
  }, [authMode, biometricAvailable]);

  const handlePasswordSubmit = async (e) => {
    e?.preventDefault();
    if (!password) { setPasswordError('Please enter your master password.'); return; }
    setPasswordBusy(true);
    setPasswordError('');
    try {
      await reauthenticateWithMasterPassword(password);
      if (enableBiometricsNext && biometricAvailable) {
        localStorage.setItem('pm_biometric_enabled', 'true');
      }
      if (navigator.vibrate) navigator.vibrate([40, 60, 100]);
      onUnlock?.();
    } catch {
      setPasswordError('Incorrect master password. Please try again.');
    } finally {
      setPasswordBusy(false);
    }
  };

  const userInitial = user?.email ? user.email.charAt(0).toUpperCase() : 'U';

  return (
    <div className={`v-lock-screen-wrapper ${isDark ? 'is-dark' : 'is-light'} animate-fade-in`}>
      <div className="v-lock-backdrop" />

      <div className="v-lock-card animate-scale-up">
        {/* Status Header */}
        <div className="v-lock-header">
          <div className={`v-lock-badge ${scanSuccess ? 'unlocked' : 'locked'}`}>
            {scanSuccess ? <Unlock size={13} className="v-lock-icon-unlocked" /> : <Lock size={13} className="v-lock-icon-locked" />}
            <span>{scanSuccess ? 'Unlocked' : 'Vault Locked'}</span>
          </div>
          <div className="v-lock-os-badge">
            <Smartphone size={11} />
            <span>Device Auth</span>
          </div>
        </div>

        {/* User Profile */}
        <div className="v-lock-user-profile">
          <div className="v-lock-avatar"><span>{userInitial}</span></div>
          <div className="v-lock-user-meta">
            <h2 className="v-lock-user-email">{user?.email || 'CipherVault User'}</h2>
            <p className="v-lock-session-note">Locked for your security</p>
          </div>
        </div>

        {/* Auth mode toggle — only shown if device has biometric */}
        {biometricAvailable && (
          <div className="v-biometric-type-selector">
            <button type="button" className={`v-bio-type-btn ${authMode === 'biometric' ? 'active' : ''}`}
              onClick={() => { setAuthMode('biometric'); setScanError(''); }}>
              <Fingerprint size={13} /><span>Biometrics</span>
            </button>
            <button type="button" className={`v-bio-type-btn ${authMode === 'password' ? 'active' : ''}`}
              onClick={() => { setAuthMode('password'); setPasswordError(''); }}>
              <KeyRound size={13} /><span>Password</span>
            </button>
          </div>
        )}

        {/* BIOMETRIC MODE */}
        {authMode === 'biometric' && biometricAvailable ? (
          <div className="v-biometric-section animate-fade-in">
            {/* Native auth prompt area — no fake scanner graphic */}
            <div className="native-auth-prompt-box">
              {scanSuccess ? (
                <div className="v-scan-success-icon animate-pop-in">
                  <CheckCircle2 size={44} color="#10b981" strokeWidth={2.5} />
                </div>
              ) : isScanning ? (
                <div className="native-auth-scanning">
                  <div className="native-auth-pulse-ring" />
                  <ScanFace size={40} className="native-auth-icon v-pulsing" />
                </div>
              ) : (
                <div className="native-auth-idle" onClick={triggerBiometricScan} role="button" tabIndex={0}
                  title={`Tap to unlock with ${biometricLabel}`}
                  onKeyDown={(e) => e.key === 'Enter' && triggerBiometricScan()}>
                  <Fingerprint size={44} className="native-auth-icon" />
                </div>
              )}
            </div>

            <div className="v-scanner-label">
              {scanSuccess ? (
                <span className="v-label-success">Authenticated! Unlocking...</span>
              ) : isScanning ? (
                <span className="v-label-scanning">
                  <RefreshCw size={13} className="et-spin" />&nbsp;Follow the prompt on your device...
                </span>
              ) : (
                <span className="v-label-prompt">Tap to unlock with {biometricLabel}</span>
              )}
            </div>

            {scanError && (
              <div className="v-scan-error-alert animate-shake">
                <AlertCircle size={14} /><span>{scanError}</span>
              </div>
            )}

            <div className="v-biometric-actions">
              <button type="button" className="v-btn-primary-scan" disabled={isScanning || scanSuccess} onClick={triggerBiometricScan}>
                {isScanning ? (
                  <><RefreshCw size={14} className="et-spin" /><span>Scanning...</span></>
                ) : (
                  <><ScanFace size={15} /><span>Unlock with {biometricLabel}</span></>
                )}
              </button>
              <button type="button" className="v-btn-switch-auth" onClick={() => { setAuthMode('password'); setScanError(''); }}>
                <KeyRound size={13} /><span>Use Master Password</span>
              </button>
            </div>
          </div>
        ) : (
          /* PASSWORD MODE */
          <form onSubmit={handlePasswordSubmit} className="v-password-form animate-fade-in">
            <div className="v-form-group">
              <label className="v-form-label">Master Password</label>
              <div className="v-password-input-wrap">
                <input type={showPassword ? 'text' : 'password'} className="v-password-input"
                  placeholder="Enter master password" value={password}
                  onChange={(e) => setPassword(e.target.value)} autoFocus autoComplete="current-password" />
                <button type="button" className="v-eye-btn" onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}>
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {passwordError && (
              <div className="v-scan-error-alert animate-shake">
                <AlertCircle size={14} /><span>{passwordError}</span>
              </div>
            )}

            {/* iOS-style toggle — only shown when device actually supports biometrics */}
            {biometricAvailable && (
              <label className="v-bio-toggle-row">
                <div className="v-bio-toggle-info">
                  <span className="v-bio-toggle-title">Enable {biometricLabel}</span>
                  <span className="v-bio-toggle-sub">Unlock directly without password</span>
                </div>
                <div className={`v-ios-switch ${enableBiometricsNext ? 'checked' : ''}`}>
                  <input type="checkbox" checked={enableBiometricsNext} onChange={(e) => setEnableBiometricsNext(e.target.checked)} className="v-sr-only" />
                  <span className="v-ios-slider" />
                </div>
              </label>
            )}

            <button type="submit" className="v-btn-primary-scan" disabled={passwordBusy || !password}>
              {passwordBusy ? (
                <><RefreshCw size={14} className="et-spin" /><span>Verifying...</span></>
              ) : (
                <><Unlock size={15} /><span>Unlock Vault</span></>
              )}
            </button>

            {biometricAvailable && (
              <button type="button" className="v-btn-switch-auth" onClick={() => { setAuthMode('biometric'); setPasswordError(''); triggerBiometricScan(); }}>
                <ScanFace size={14} /><span>Use {biometricLabel}</span>
              </button>
            )}
          </form>
        )}

        {/* Footer Logout */}
        <div className="v-lock-footer">
          <button type="button" className="v-lock-logout-link" onClick={logout}>
            <LogOut size={13} /><span>Sign out of account</span>
          </button>
        </div>
      </div>
    </div>
  );
}
